// HOW FAR THESE RECORDS HAVE CAUGHT UP WITH THE REAL STATE OF THE SITE.
//
// Owner, 2026-10-06: "add on the digests, a disclamer on top ... status report
// is cought up on real time project state at 30% then wehn we update it on
// coordination page, it should take the enw percentage until it hits %100,
// then it will add, real time proejct status in Green". Then 2026-10-07, after
// finding it nowhere: "in edit schedule, i don't see the decay percentage of
// cought up to reat state of proejct", and where he wants it - "just after the
// Schedule - Project name editable field/slider with current status accuracy".
//
// WHY THE NUMBER EXISTS AT ALL. Every figure in a digest - percent installed,
// activities late, issues open - is only as true as the last time somebody
// updated the records. A reader has no way to know whether they are looking at
// yesterday's site or last month's. This is the one number that says so, and it
// DECAYS a point a week on the worker, because being level with the site is not
// a state you reach, it is a state that expires.
//
// THREE STATES, AND THE THIRD IS THE ONE THAT MATTERS:
//   not assessed  nobody has judged it. NOT zero and not a hundred - inventing
//                 either would be a claim nobody made, and it is the state
//                 every project on the platform is in until somebody moves
//                 this slider.
//   a percentage  amber: the records are this far caught up.
//   100           green: real time project status.
//
// A SLIDER AND A NUMBER, not one or the other. The slider is how somebody
// judges "about a third"; the box is how they type 30 when they mean 30.
import React, { useCallback, useEffect, useState } from "react";
import { Button, InputNumber, Slider, Tooltip, message } from "antd";
import { getReportingCatchUp, setReportingCatchUp } from "api/reportingCatchUp";

const GREEN = "#17803d";
const AMBER = "#b54708";
const GREY = "#667085";

// The same three sentences the digest prints, so the page and the email never
// disagree about what the number means. utils/reportingCatchUp is the server's
// copy; if these two ever drift, the email is right and this is wrong.
export function noticeFor(percent) {
  if (percent === null || percent === undefined) {
    return { colour: GREY, text: "Not assessed yet — the digests say so rather than guessing a number." };
  }
  const n = Math.floor(Number(percent));
  if (n >= 100) return { colour: GREEN, text: "Real time project status." };
  return { colour: AMBER, text: `Caught up ${n}% to the current state of the project.` };
}

export default function ReportingCatchUp({ projectId, compact = false, onChange }) {
  const [state, setState] = useState({ loading: true, percent: null, mayChange: false, error: "" });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    getReportingCatchUp(projectId)
      .then((d) => {
        setState({ loading: false, percent: d.percent === undefined ? null : d.percent, mayChange: !!d.mayChange, error: "" });
        setDraft(d.percent === undefined ? null : d.percent);
      })
      .catch((e) => setState({ loading: false, percent: null, mayChange: false, error: (e && (e.err || e.message)) || "" }));
  }, [projectId]);

  useEffect(load, [load]);

  const save = (value) => {
    setSaving(true);
    setReportingCatchUp(projectId, value)
      .then((d) => {
        const p = d && d.percent !== undefined ? d.percent : value;
        setState((s) => ({ ...s, percent: p }));
        setDraft(p);
        message.success(p === null ? "Set back to not assessed" : `Reporting catch-up set to ${p}%`);
        if (onChange) onChange(p);
      })
      // THE SERVER'S REFUSAL, IN ITS OWN WORDS. It refuses out-of-range rather
      // than clamping - a clamp would turn 300 into 100, which is the one
      // value that makes the digest claim the report is real-time.
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not save that"))
      .finally(() => setSaving(false));
  };

  if (state.loading) return null;
  const notice = noticeFor(state.percent);
  const dirty = Number(draft) !== Number(state.percent) || (draft === null) !== (state.percent === null);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: compact ? 0 : 6 }}>
      <Tooltip title="Every number in this project's reports is only as true as the last time the records were updated. This is how far they have caught up — and it loses a point a week until somebody raises it again.">
        <span style={{ color: GREY, fontSize: 13, whiteSpace: "nowrap" }}>Current status accuracy</span>
      </Tooltip>

      {state.mayChange ? (
        <>
          <Slider
            style={{ width: compact ? 140 : 200, margin: 0 }}
            min={0} max={100} step={1}
            value={draft === null ? 0 : Number(draft)}
            onChange={(v) => setDraft(v)}
            tooltip={{ formatter: (v) => `${v}%` }}
          />
          <InputNumber
            size="small" min={0} max={100} style={{ width: 72 }}
            value={draft === null ? undefined : Number(draft)}
            placeholder="—"
            formatter={(v) => (v === "" || v === undefined ? "" : `${v}%`)}
            parser={(v) => String(v || "").replace("%", "")}
            onChange={(v) => setDraft(v === null || v === undefined ? null : v)}
          />
          <Button size="small" type="primary" loading={saving} disabled={!dirty} onClick={() => save(draft)}>
            Save
          </Button>
          {/* Clearing it is a real choice, not a zero. A project nobody has
              judged must be able to go back to saying so. */}
          {state.percent !== null && (
            <Button size="small" type="link" disabled={saving} onClick={() => save(null)}>
              Not assessed
            </Button>
          )}
        </>
      ) : null}

      <span style={{ color: notice.colour, fontSize: 13, fontWeight: state.percent >= 100 ? 600 : 500 }}>
        {notice.text}
      </span>
    </div>
  );
}
